let modalEl: HTMLElement | null = null;
let videoEl: HTMLVideoElement | null = null;
let stream: MediaStream | null = null;
let currentResolve: ((file: File | null) => void) | null = null;

type CaptureHandler = (file: File) => void;

export async function openWebcamModal(onCapture: CaptureHandler): Promise<void> {
  ensureModal();
  if (!modalEl || !videoEl) return;

  modalEl.classList.remove('hidden');
  setStatus('Requesting camera…');

  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 1280 } },
      audio: false,
    });
    videoEl.srcObject = stream;
    await videoEl.play();
    setStatus('');
  } catch (err) {
    setStatus(`Camera error: ${(err as Error).message || 'Unable to access camera'}`);
    return;
  }

  const file = await new Promise<File | null>((resolve) => {
    currentResolve = resolve;
  });

  if (file) onCapture(file);
}

function ensureModal(): void {
  if (modalEl) return;

  modalEl = document.createElement('div');
  modalEl.className = 'webcam-modal hidden';
  modalEl.innerHTML = `
    <div class="webcam-dialog">
      <div class="webcam-header">
        <span>Webcam Capture</span>
        <button type="button" class="webcam-close" aria-label="Close">×</button>
      </div>
      <div class="webcam-stage">
        <video class="webcam-video" playsinline muted></video>
        <div class="webcam-status"></div>
      </div>
      <div class="webcam-actions">
        <button type="button" class="webcam-cancel">Cancel</button>
        <button type="button" class="webcam-capture">Capture</button>
      </div>
    </div>
  `;
  document.body.appendChild(modalEl);

  videoEl = modalEl.querySelector<HTMLVideoElement>('.webcam-video');

  const close = (): void => closeModal(null);
  modalEl.querySelector('.webcam-close')?.addEventListener('click', close);
  modalEl.querySelector('.webcam-cancel')?.addEventListener('click', close);
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) close();
  });
  document.addEventListener('keydown', (e) => {
    if (modalEl?.classList.contains('hidden')) return;
    if (e.key === 'Escape') close();
  });

  modalEl.querySelector('.webcam-capture')?.addEventListener('click', () => {
    if (!videoEl) return;
    const canvas = document.createElement('canvas');
    const w = videoEl.videoWidth;
    const h = videoEl.videoHeight;
    if (!w || !h) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(videoEl, 0, 0, w, h);
    canvas.toBlob((blob) => {
      if (!blob) {
        closeModal(null);
        return;
      }
      const file = new File([blob], `webcam-${Date.now()}.png`, { type: 'image/png' });
      closeModal(file);
    }, 'image/png');
  });
}

function setStatus(text: string): void {
  if (!modalEl) return;
  const statusEl = modalEl.querySelector<HTMLElement>('.webcam-status');
  if (statusEl) statusEl.textContent = text;
}

function closeModal(file: File | null): void {
  if (stream) {
    for (const track of stream.getTracks()) track.stop();
    stream = null;
  }
  if (videoEl) videoEl.srcObject = null;
  modalEl?.classList.add('hidden');
  setStatus('');
  if (currentResolve) {
    currentResolve(file);
    currentResolve = null;
  }
}
